import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight, Shield, BadgeCheck, Wallet } from "lucide-react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

const DEFAULT_SEAT_CAP = 50;

function Pill({ children }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3 py-1 text-xs text-secondary">
      {children}
    </span>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-line bg-surface p-4">
      <div className="text-sm text-faint">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-ink">{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="text-xl font-semibold text-ink">{title}</h2>
      <div className="mt-3 text-secondary leading-relaxed">{children}</div>
    </section>
  );
}

function BulletList({ items }) {
  return (
    <ul className="mt-3 space-y-2">
      {items.map((x, idx) => (
        <li key={idx} className="flex gap-3 text-secondary">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
          <span>{x}</span>
        </li>
      ))}
    </ul>
  );
}

function Money({ value }) {
  const n = Number(value || 0);
  return (
    <span>
      $
      {n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}
    </span>
  );
}

function formatMonth(iso) {
  // iso like "2025-12-01"
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", year: "numeric" });
}

function mergeMonths(monthly) {
  const map = new Map();

  const ingest = (arr, key) => {
    (arr || []).forEach((r) => {
      const m = r.month;
      if (!map.has(m))
        map.set(m, { month: m, paid: 0, pending: 0, available: 0 });
      map.get(m)[key] = Number(r.total || 0);
    });
  };

  ingest(monthly?.paid, "paid");
  ingest(monthly?.pending, "pending");
  ingest(monthly?.available, "available");

  // sort newest first
  return Array.from(map.values()).sort((a, b) => (a.month > b.month ? -1 : 1));
}

export default function PartnerProgramPage() {
  const [me, setMe] = useState(null);
  const [loadingMe, setLoadingMe] = useState(true);

  const [monthly, setMonthly] = useState(null);
  const [loadingMonthly, setLoadingMonthly] = useState(true);

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoadingMe(true);
      try {
        const res = await fetchWithAuth("/api/accounts/me/");
        if (mounted) setMe(res);
      } catch {
        // Not logged in or endpoint not reachable; this is fine for public view
        if (mounted) setMe(null);
      } finally {
        if (mounted) setLoadingMe(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // Pull seats from backend if present
  const seatCap = useMemo(() => {
    const cap = Number(me?.partner_seat_cap ?? DEFAULT_SEAT_CAP);
    return Number.isFinite(cap) && cap > 0 ? cap : DEFAULT_SEAT_CAP;
  }, [me]);

  const seatsRemaining = useMemo(() => {
    const raw = me?.partner_seats_remaining;
    if (raw === undefined || raw === null) return null; // unknown
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [me]);

  const isPartner = Boolean(me?.is_partner);
  const partnerIsActive = Boolean(me?.partner_is_active);

  // Step 2: monthly summaries (only attempt if partner)
  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoadingMonthly(true);
      try {
        // Only partners should see this data; if not a partner, skip fetch
        if (!isPartner) {
          if (mounted) setMonthly(null);
          return;
        }

        const data = await fetchWithAuth("/api/partners/summary/monthly/?months=12");
        if (mounted) setMonthly(data);
      } catch (e) {
        console.warn("Monthly partner summary not available yet:", e);
        if (mounted) setMonthly(null);
      } finally {
        if (mounted) setLoadingMonthly(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isPartner]);

  const monthlyRows = useMemo(() => mergeMonths(monthly), [monthly]);

  const paidTotal = useMemo(() => {
    const rows = monthly?.paid || [];
    return rows.reduce((acc, r) => acc + Number(r.total || 0), 0);
  }, [monthly]);

  const pendingTotal = useMemo(() => {
    const rows = monthly?.pending || [];
    return rows.reduce((acc, r) => acc + Number(r.total || 0), 0);
  }, [monthly]);

  const seatsLabel = useMemo(() => {
    if (loadingMe) return "Checking…";
    if (seatsRemaining === null) return "Invite-only";
    if (seatsRemaining <= 0) return "Seats full";
    return `${seatsRemaining} left`;
  }, [loadingMe, seatsRemaining]);

  return (
    <div className="min-h-screen bg-bg text-ink">
      <div className="max-w-5xl mx-auto px-6 py-14">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="flex flex-col gap-6"
        >
          <div className="flex flex-wrap items-center gap-3">
            <Pill>
              <Lock className="h-4 w-4" />
              Invite-only · Manual approval
            </Pill>
            <Pill>
              <BadgeCheck className="h-4 w-4" />
              20% of Sift transaction fees
            </Pill>
            <Pill>
              <Shield className="h-4 w-4" />
              Not an affiliate program
            </Pill>
          </div>

          <div className="space-y-3">
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight">
              Sift Partner Program
            </h1>
            <p className="text-secondary text-lg leading-relaxed max-w-3xl">
              Share in the value you help create.
              <br />
              Sift is infrastructure for running contracts, payments, and compliance in one place.
              We don’t sell leads. We don’t sell licenses. We participate in real economic activity.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
            <Stat label="Partner seats (initial)" value={`${seatCap}`} />
            <Stat label="Seats remaining" value={seatsLabel} />
            <Stat label="Your share" value="20% of Sift’s 10%" />
          </div>

          <div className="rounded-2xl border border-line bg-surface p-6 mt-2">
            <div className="flex items-start justify-between gap-6 flex-col md:flex-row">
              <div className="max-w-3xl">
                <div className="text-sm text-faint">What this is</div>
                <div className="mt-2 text-secondary leading-relaxed">
                  Partners are paid based on <span className="text-ink">economic flow</span>, not signups.
                  You’re not an influencer or a coupon pusher.
                  You’re a distribution node and trust broker, like a Stripe partner, not an affiliate link.
                </div>
              </div>

              <div className="flex gap-3">
                {isPartner ? (
                  <Link
                    to="/dashboard/partner"
                    className="inline-flex items-center gap-2 rounded-xl bg-brand text-white px-4 py-2 text-sm font-semibold hover:bg-brand-hover transition"
                  >
                    {partnerIsActive ? "Partner Dashboard" : "Partner Setup"}{" "}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : (
                  // NOTE: this assumes /partner/apply exists as a PUBLIC route.
                  // If not, change this to "/dashboard/partner" and your dashboard page will show the apply flow.
                  <Link
                    to="/partner/apply"
                    className="inline-flex items-center gap-2 rounded-xl bg-brand text-white px-4 py-2 text-sm font-semibold hover:bg-brand-hover transition"
                  >
                    Apply to Become a Partner <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
            </div>
          </div>

          <Section title="How partners earn">
            <BulletList
              items={[
                "Sift charges a fixed 10% transaction fee when payments move through the platform.",
                "Approved partners earn 20% of Sift’s transaction fees. (It’s fixed, just like the 10% platform fee.)",
                "Revenue share is calculated on completed transactions only.",
                "Partner earnings are paid monthly.",
              ]}
            />
            <div className="mt-4 rounded-xl border border-line bg-sunken p-4 text-secondary">
              <span className="text-ink font-semibold">Example:</span>{" "}
              If $100,000 flows through Sift, Sift earns $10,000. A partner earning 20% receives $2,000.
            </div>
          </Section>

          {/* ===========================
              STEP 2: Monthly summaries
              =========================== */}
          <Section title="Monthly payout summaries">
            {!isPartner ? (
              <div className="rounded-2xl border border-line bg-surface p-5 text-secondary">
                Partner payout summaries appear here once you’re approved and active.
              </div>
            ) : loadingMonthly ? (
              <div className="rounded-2xl border border-line bg-surface p-5 text-secondary">
                Loading monthly payout summaries…
              </div>
            ) : !monthly ? (
              <div className="rounded-2xl border border-line bg-surface p-5 text-secondary">
                No payout data yet. Once your introduced organizations process payments through Sift,
                you’ll see paid and pending commissions here.
              </div>
            ) : (
              <div className="rounded-2xl border border-line bg-surface p-5">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                  <div>
                    <div className="text-sm text-faint">Last 12 months</div>
                    {monthly?.range_start ? (
                      <div className="text-xs text-faint mt-1">
                        From {monthly.range_start}
                      </div>
                    ) : null}
                  </div>

                  <div className="flex gap-3">
                    <div className="rounded-xl border border-line bg-sunken px-4 py-3">
                      <div className="text-xs text-faint">Paid</div>
                      <div className="text-lg font-semibold">
                        <Money value={paidTotal} />
                      </div>
                    </div>
                    <div className="rounded-xl border border-line bg-sunken px-4 py-3">
                      <div className="text-xs text-faint">Pending</div>
                      <div className="text-lg font-semibold">
                        <Money value={pendingTotal} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-faint border-b border-line">
                        <th className="py-2 pr-4">Month</th>
                        <th className="py-2 pr-4">Paid</th>
                        <th className="py-2 pr-4">Pending</th>
                        <th className="py-2 pr-4">Available soon</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyRows.length ? (
                        monthlyRows.map((row) => (
                          <tr key={row.month} className="border-b border-line">
                            <td className="py-3 pr-4 text-secondary">
                              {formatMonth(row.month)}
                            </td>
                            <td className="py-3 pr-4 text-secondary">
                              <Money value={row.paid} />
                            </td>
                            <td className="py-3 pr-4 text-secondary">
                              <Money value={row.pending} />
                            </td>
                            <td className="py-3 pr-4 text-secondary">
                              <Money value={row.available} />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="py-4 text-faint">
                            No monthly rows yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="mt-3 text-xs text-faint">
                  Paid totals reflect commissions marked <span className="text-faint">paid</span>.
                  Pending totals reflect commissions not yet paid.
                </div>
              </div>
            )}
          </Section>

          <Section title="What makes this different">
            Sift rewards outcomes. Partners win when organizations actually run contracts through Sift, payments are processed, and volume grows over time.
            This creates long-term alignment rather than one-time commissions.
          </Section>

          <Section title="Who this is for">
            <BulletList
              items={[
                "Consultants, operators, and advisors",
                "Sales professionals with trusted networks",
                "Industry insiders who understand operational pain",
                "People who can introduce decision-makers, not just users",
              ]}
            />
            <div className="mt-4 text-faint">
              <div className="font-semibold text-secondary">Not for:</div>
              <BulletList
                items={[
                  "Coupon or promo sites",
                  "Mass affiliate marketers",
                  "Influencer traffic without operational context",
                ]}
              />
            </div>
          </Section>

          <Section title="Guardrails">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="rounded-2xl border border-line bg-surface p-5">
                <div className="flex items-center gap-2 text-ink font-semibold">
                  <Wallet className="h-5 w-5" />
                  Partners may
                </div>
                <BulletList
                  items={[
                    "Introduce organizations to Sift",
                    "Share product demos and materials",
                    "Help organizations onboard and run their first contracts",
                    "Earn revenue share from transaction volume",
                  ]}
                />
              </div>

              <div className="rounded-2xl border border-line bg-surface p-5">
                <div className="flex items-center gap-2 text-ink font-semibold">
                  <Shield className="h-5 w-5" />
                  Partners may not
                </div>
                <BulletList
                  items={[
                    "Set or negotiate pricing",
                    "Modify fees or terms",
                    "Promise product features or roadmap changes",
                    "Represent themselves as Sift employees",
                    "Bind Sift to any agreement",
                  ]}
                />
              </div>
            </div>
          </Section>

          <Section title="Attribution & transparency">
            Partner attribution is handled through referral codes or direct attribution by Sift for warm introductions.
            Revenue share is based solely on transaction volume processed by attributed accounts.
            <div className="mt-2 text-faint">All calculations are transparent and auditable.</div>
          </Section>

          <Section title="Lifetime economics">
            Approved partners receive their revenue share for the lifetime of the attributed organization’s usage of Sift.
            <div className="mt-2 text-faint">
              Upon a partner&apos;s death, all economic interest occurring after that time will revert back to Sift.
            </div>
          </Section>

          <Section title="Approval & participation">
            Participation requires approval.
            <div className="mt-2 text-faint">
              Sift reserves the right to approve, decline, or terminate future participation at any time. Termination affects future participation only
              and does not claw back earnings already accrued on completed transactions.
            </div>
          </Section>

          <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 rounded-2xl border border-line bg-surface p-6">
            <div>
              <div className="text-ink font-semibold">Ready to apply?</div>
              <div className="text-faint mt-1">
                If you can introduce organizations that will move real volume through Sift, apply here.
              </div>
            </div>
            <Link
              to="/partner/apply"
              className="inline-flex items-center gap-2 rounded-xl bg-brand text-white px-5 py-2 text-sm font-semibold hover:bg-brand-hover transition"
            >
              Apply to Become a Partner <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-8 text-xs text-faint">
            © {new Date().getFullYear()} Sift Careers. All rights reserved.
          </div>
        </motion.div>
      </div>
    </div>
  );
}
